// src/stores/auth.js
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import app from 'configs/appResolver';
import { toCapitalCase } from 'utils/helpers';
import apiConfig from 'configs/fetchInterceptor';
import useZustand from 'hooks/useZustand';
import { logErrorSilent } from 'utils/errorLogger';
import { buildAppConfigurations } from 'configs/ConfigIndex';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const localStorageKey = 'storage-auth';

// *******************************************************************************
// INITIAL STATE HYDRATION
// *******************************************************************************
const getInitialToken = () => {
  if (typeof window === 'undefined') return null;
  try {
    const field = `${app.ID}:storage-auth`;
    const lsData = JSON.parse(localStorage.getItem(field));
    return lsData?.token || null;
  } catch (e) {
    return null;
  }
};

// *******************************************************************************
// STATE
// *******************************************************************************
export const initialState = {
  token: getInitialToken(),
  user: null,
  subscriptions: [], // array de assinaturas ativas (status === 2)
  subscription: null, // assinatura selecionada como default
  companies: [], // empresas da assinatura selecionada
  company: null, // empresa selecionada como default
  genericPerson: null, // entity generica com entityType = person
  genericCompany: null, // entity generica com entityType = company
  team: [], // usuários que acessam a assinatura selecionada
  me: null, // meu objeto no team (team.accountId === user._id)
  mainCompany: null,
  availableRoutes: [],
  fn: {
    refreshSubscriptions: () => {},
  },
};

// *******************************************************************************
// METHODS
// *******************************************************************************
function buildStorageKey() {
  return `${app.ID}:${localStorageKey}`;
}

export function getStorageAuthItem(key) {
  const field = buildStorageKey();
  let lsData;
  try {
    lsData = JSON.parse(localStorage.getItem(field));
  } catch (e) {
    logErrorSilent(e, {
      context: 'authStore.getStorageAuthItem (JSON.parse)',
      extraData: { field, key },
    });
  }
  return lsData?.[key];
}

export function setStorageAuthItem(key, value) {
  const field = buildStorageKey();
  let lsData = {};
  try {
    lsData = JSON.parse(localStorage.getItem(field)) || {};
  } catch (e) {
    lsData = {};
  }
  if (value) {
    lsData[key] = value;
  } else {
    delete lsData[key];
  }
  localStorage.setItem(field, JSON.stringify(lsData));
}

export function removeStorageAuthItem(key) {
  const field = buildStorageKey();
  let lsData = {};
  try {
    lsData = JSON.parse(localStorage.getItem(field)) || {};
  } catch (e) {
    lsData = {};
  }
  if (lsData.hasOwnProperty(key)) {
    delete lsData[key];
    localStorage.setItem(field, JSON.stringify(lsData));
  }
}

function decorateFirebaseUser(user, provider, service) {
  return {
    ...user,
    provider,
    service,
    name: user?.name || toCapitalCase(user.email.split('@')[0]),
    emailConfirmation: user?.emailConfirmation || false,
    mobileConfirmation: user?.mobileConfirmation || false,
  };
}

// *******************************************************************************
// STORE
// *******************************************************************************
const store = create(
  devtools(
    (set, get) => ({
      ...initialState,

      setState: (storedState = {}) => {
        const currentState = get().storedState || {};
        set({ fn: { ...currentState, ...storedState } });
      },

      setToken: value => {
        setStorageAuthItem('token', value);
        apiConfig.defaults.headers['authorization'] = value ? `Bearer ${value}` : null;
        set({ token: value });
      },

      // 1. setUser: salva o user e extrai subscriptions ativas
      setUser: userObj => {
        const setAppActivatedStore = useZustand('app', 'setAppActivated', false);

        // Filtra apenas subscriptions ativas (status === 2)
        const activeSubscriptions = userObj?.subscriptions?.filter(s => s?.status === 2) || [];
        const appActivated = activeSubscriptions.length > 0;

        setStorageAuthItem('userId', userObj?._id);
        apiConfig.defaults.headers['x-userid'] = userObj?._id || null;

        set({ user: userObj });
        setAppActivatedStore(appActivated);

        // Chama setSubscriptions com as subscriptions ativas
        get().setSubscriptions(activeSubscriptions);
      },

      // 2. setSubscriptions: salva array de subscriptions e elege default
      setSubscriptions: subscriptions => {
        const userId = get().user?._id;

        if (!subscriptions || subscriptions.length === 0) {
          // Build app configurations even without subscription
          // This populates the navigation menu (account module should always be visible)
          buildAppConfigurations(null);

          set({
            subscriptions: [],
            subscription: null,
            companies: [],
            company: null,
            genericPerson: null,
            genericCompany: null,
            team: [],
            me: null,
            mainCompany: null,
          });
          return;
        }

        // Salva subscriptions no store
        set({ subscriptions });

        // Precedência: localStorage > subscriptions[0]
        const storageKey = `userId[${userId}].subscriptionId`;
        const storedSubscriptionId = getStorageAuthItem(storageKey);

        // Busca a subscription salva no localStorage ou usa a primeira
        let defaultSubscription = null;
        if (storedSubscriptionId) {
          defaultSubscription = subscriptions.find(s => s._id === storedSubscriptionId);
        }
        if (!defaultSubscription) {
          defaultSubscription = subscriptions[0];
        }

        // Chama setSubscription passando ID e o array (para evitar problema de timing)
        if (defaultSubscription) {
          get().setSubscription(defaultSubscription._id, subscriptions);
        }
      },

      // 3. setSubscription: salva subscription, team, me, companies e dispara setCompany
      setSubscription: (subscriptionId, subscriptionsArray) => {
        const userId = get().user?._id;
        // Usa o array passado ou pega do store
        const subscriptions = subscriptionsArray || get().subscriptions;

        // Encontra a subscription pelo ID
        const subscription = subscriptions?.find(x => x?._id === subscriptionId);

        if (!subscription) {
          set({
            subscription: null,
            companies: [],
            company: null,
            genericPerson: null,
            genericCompany: null,
            team: [],
            me: null,
            mainCompany: null,
          });
          return;
        }

        // Configura a aplicação
        buildAppConfigurations(subscription);

        // Salva no localStorage
        if (userId) {
          const key = `userId[${userId}].subscriptionId`;
          setStorageAuthItem(key, subscription._id);
        }

        // Configura headers da API
        apiConfig.defaults.headers['x-subscriptionid'] = subscription._id || null;

        // Extrai team e encontra 'me'
        const team = subscription.team || [];
        const me = team.find(t => t.accountId === userId) || null;

        // Merge entity collaborator data into user object so storedUser
        // exposes professional fields (department, commission, defaultLanguage, etc.)
        const currentUser = get().user;
        const updatedUser = me?.entityData
          ? { ...currentUser, entityData: me.entityData }
          : currentUser;

        // Extrai companies
        const companies = subscription.companies || [];

        // Salva subscription, team, me, companies no store
        set({
          user: updatedUser,
          subscription,
          mainCompany: subscription.mainCompany || null,
          team,
          me,
          companies,
          genericPerson: subscription?.genericPerson || null,
          genericCompany: subscription?.genericCompany || null,
        });

        // Dispara setCompany para eleger a company default
        get().setCompany(null, companies);

        // Reset parameters and reconnect SSE with new subscriptionId
        // ALWAYS reset parameters to avoid stale data from previous subscription
        const appState = useZustand('app', 'state', false);
        const setParameters = useZustand('app', 'setParameters', false);
        if (setParameters) setParameters(null);

        // Reconnect SSE if it was already initialized (skip during initial login flow)
        if (appState?.reconnectSSE) {
          appState.reconnectSSE({
            accountId: userId,
            subscriptionId: subscription._id,
          });
        }
      },

      // 4. setCompanies: atualiza companies e dispara setCompany
      setCompanies: companies => {
        set({ companies: companies || [] });

        // Dispara setCompany para eleger a company default
        get().setCompany(null, companies);
      },

      // 5. setCompany: salva company selecionada
      setCompany: (companyId, companiesArray) => {
        const userId = get().user?._id;
        // Usa o array passado ou pega do store
        const companies = companiesArray || get().companies;

        if (!companies || companies.length === 0) {
          set({ company: null });
          return;
        }

        let company = null;

        // Se passou um ID específico, busca ele
        if (companyId) {
          company = companies.find(c => c._id === companyId);
        }

        // Se não encontrou, usa precedência: localStorage > companies[0]
        if (!company) {
          const storedCompanyId = getStorageAuthItem(`userId[${userId}].companyId`);
          if (storedCompanyId) {
            company = companies.find(c => c._id === storedCompanyId);
          }
        }
        if (!company) {
          company = companies[0];
        }

        // Salva no localStorage
        if (userId && company) {
          setStorageAuthItem(`userId[${userId}].companyId`, company._id);
        }

        // Configura headers da API
        apiConfig.defaults.headers['x-companyid'] = company?._id || null;

        set({ company: company || null });
      },

      // 6. setTeam: salva o team e atualiza 'me'
      setTeam: team => {
        const userId = get().user?._id;
        const me = team?.find(t => t.accountId === userId) || null;

        // Keep user.entityData in sync with collaborator data
        const currentUser = get().user;
        const updatedUser = me?.entityData
          ? { ...currentUser, entityData: me.entityData }
          : currentUser;

        set({ user: updatedUser, team: team || [], me });
      },

      // 7. syncSubscriptionTeam: sincroniza o team de uma subscription específica
      // (no array subscriptions, em user.subscriptions, na subscription ativa
      // se bater o id, e no team/me). SEM reconstruir rotas nem re-logar —
      // atualização targetada. Consumidores possíveis do team atualizado:
      // - TeamTab (via data.team do DocumentContext)
      // - CardHeaderTeamMembers (via storedUser.subscriptions[].team)
      // - Topbar subscription combo
      syncSubscriptionTeam: (subscriptionId, team) => {
        const state = get();
        const userId = state.user?._id;
        const nextTeam = team || [];

        // Atualiza o item no array de subscriptions (store-level)
        const nextSubscriptions = (state.subscriptions || []).map(s =>
          s?._id === subscriptionId ? { ...s, team: nextTeam } : s
        );

        // Atualiza tambem o espelho em user.subscriptions — alguns componentes
        // leem de storedUser?.subscriptions em vez de storedSubscriptions
        const nextMe = nextTeam.find(t => t?.accountId === userId) || null;
        const baseUser = state.user?.subscriptions
          ? {
              ...state.user,
              subscriptions: state.user.subscriptions.map(s =>
                s?._id === subscriptionId ? { ...s, team: nextTeam } : s
              ),
            }
          : state.user;

        // Keep entityData in sync with the active subscription's collaborator
        const isActiveForEntity = state.subscription?._id === subscriptionId;
        const nextUser = isActiveForEntity && nextMe?.entityData
          ? { ...baseUser, entityData: nextMe.entityData }
          : baseUser;

        // Se a subscription editada e a ativa, atualiza subscription/team/me
        const isActive = state.subscription?._id === subscriptionId;
        const nextActiveSubscription = isActive
          ? { ...state.subscription, team: nextTeam }
          : state.subscription;
        const nextActiveTeam = isActive ? nextTeam : state.team;
        const nextActiveMe = isActive ? nextMe : state.me;

        set({
          user: nextUser,
          subscriptions: nextSubscriptions,
          subscription: nextActiveSubscription,
          team: nextActiveTeam,
          me: nextActiveMe,
        });
      },

      setMainCompany: value => set({ mainCompany: value || null }),

      setAvailableRoutes: value => {
        set({ availableRoutes: value });
      },
    }),
    {
      name: 'authStore',
      enabled: true,
    }
  )
);

// *******************************************************************************
// EXPORTS
// *******************************************************************************
export const useAuthStore = store;
// export const authStore = store.getState;
// export const fromAuthStore = sel => sel(authStore());

// *******************************************************************************
// SELECTORS (For non-React contexts)
// *******************************************************************************
export const fromAuthStore = selector => {
  if (typeof selector === 'function') {
    return selector(useAuthStore.getState());
  }
  return useAuthStore.getState();
};
